import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import ThemedModal from "components/atoms/ThemedModal";
import ThemedList from "components/atoms/ThemedList";
import Text from "theme/Text";
import ListItem from 'theme/ListItem';
import Spinner from "theme/Spinner";
import FetchEmployee from "utils/fetch/FetchEmployee";
import LoadingComponent from "components/atoms/Loading";
import { employeeConstants } from "utils/constants/employee-constants";
import Tag, { tagAppearances } from "components/atoms/Tag";
import { constantsActive } from "utils/constants/constants";
import { Linking, StyleSheet, View } from "react-native";
import i18n from "utils/i18n";
import MutateScan from 'utils/mutate/scan'
import { scanThroughConstants } from "utils/constants/scan-constants";
import EmployeeCard, { EmployeeCardModes } from "components/molecules/EmployeeCard";
import HelperString from 'utils/helper-string'
import { useNavigation } from '@react-navigation/native'
import { openUrl } from 'utils/helper-url'
import { colorList } from "utils/colors";
import { meetingTimeslots } from 'utils/helper-management-dashboard';
import useForegroundPolling from 'utils/hooks/use-foreground-polling';
import HelperManagementRights from 'utils/helper-management-rights';
import { managementRightsConstants } from 'utils/constants/management-right-constants';
import Translate from 'utils/translate';
import Button from 'theme/Button';

const constantsPhonenumbers = {
	PHONENUMBER: 'phonenumber',
	LANDLINE_PHONENUMBER: 'landline_phonenumber'
}

const ManagementDashboardModalMeetingRoomDetails = ( { building = null, room = null, timeslot = null, timeslots= null, bookings = null, onClose = null } ) => {

	const navigation = useNavigation();

	const [ scanInMutating, setScanInMutating ] = useState(false);
	const mounted = useRef(true);
	useEffect( () => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const visibleTimeslots = useMemo( () => {
		return meetingTimeslots(timeslot, timeslots, bookings);
	}, [ timeslot, timeslots, bookings ]);
	const employeeGuids = _.uniq(visibleTimeslots.flatMap( ( slot ) => {
		return (bookings?.[slot.guid] ?? []).map( ( booking ) => {
			return booking.employee?.guid;
		});
	})).filter(Boolean);
	const { data: employeeResult, loading: visibleEmployeesLoading, error, retry: fetchEmployees } = useForegroundPolling({
		requestKey: JSON.stringify([room.guid, employeeGuids]),
		request: ( callbacks ) => {
			if( employeeGuids.length === 0 ) {
				callbacks.callbackSuccess({ employees: [] });
				return;
			}
			FetchEmployee.fetchAll({
				guid: employeeGuids,
				dataSet: 'complex',
				variables: {
					visible_for_employees: [constantsActive.YES, constantsActive.NO],
					kind: [employeeConstants.KIND_INTERNAL, employeeConstants.KIND_EXTERNAL],
				},
				...callbacks,
			});
		},
	});
	const visibleEmployees = employeeResult?.employees ?? [];
	const startEndTime = {
		start: visibleTimeslots[0]?.timeslot_start ?? timeslot.timeslot_start,
		end: visibleTimeslots[visibleTimeslots.length - 1]?.timeslot_end ?? timeslot.timeslot_end,
	};
	const canScan = HelperManagementRights.hasEditRight(managementRightsConstants.EDIT.BOOKINGS_MEETING_ROOMS_SCAN_IN);

	const callPhone = async ( { employee = {}, which = null } ) => {
		const phonenumber = employee[which];
		if( phonenumber ) {
			try {
				if( await Linking.canOpenURL(`tel:${phonenumber}`) ) {
					await openUrl(`tel:${phonenumber}`);
				}
			} catch( error ) {
				// Unsupported phone handlers must not leave an unhandled rejection.
			}
		}
	}

	const scanIn = ( { employee = null, callbackStart = null, callbackSuccess = null, callbackError = null } ) => {
		MutateScan.scanIn( `building:${building.guid}`, {
			employeeGuid: employee.guid,
			scannedInThrough: scanThroughConstants.QRCODE,
			callbackStart: callbackStart,
			callbackSuccess: callbackSuccess,
			callbackError: callbackError
		});
	}

	const scanOut = ( { employee = null, callbackStart = null, callbackSuccess = null, callbackError = null } ) => {
		MutateScan.scanOut( `building:${building.guid}`, {
			employeeGuid: employee.guid,
			scannedOutThrough: scanThroughConstants.QRCODE,
			callbackStart: callbackStart,
			callbackSuccess: callbackSuccess,
			callbackError: callbackError
		});
	}

	// Rendering
	const title = `${ Translate.field(building, 'title') } - ${ Translate.field(room, 'title') }`;
	const subtitle = `${ startEndTime.start.substr( 0, 5 ) } - ${ startEndTime.end.substr( 0, 5 ) }`;

	const renderItem = ( { item = [], index = 0, seperators = {} } ) => {
		let scanInButton = null;
		const hints = [];

		if( visibleEmployeesLoading === false ) {
			if( _.get( item, 'is_in_office', constantsActive.NO ) === constantsActive.YES ) {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-inofficeyes`, item ) }
						appearance={ tagAppearances.GREEN }
						style={ styles.tag }>

						{ i18n.t( 'basic.present_yes' ) }
					</Tag>
				);
			} else {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-inofficeno`, item ) }
						appearance={ tagAppearances.RED }
						style={ styles.tag }>

						{ i18n.t( 'basic.present_no' ) }
					</Tag>
				);
			}
		} else {
			hints.push(
				<Tag
					key={ HelperString.hash( `meeting-room-details-tag-offie-loading`, item ) }
					appearance={ tagAppearances.GREY }
					style={ styles.tag }>

					<Spinner
						style={ styles.spinnerWidth10Height10 }
						color={ colorList.PRIMARY.primary }
					/>
				</Tag>
			);
		}

		if( _.get( item, 'kind', employeeConstants.KIND_INTERNAL) === employeeConstants.KIND_EXTERNAL && canScan ) {
			if( scanInMutating === false ) {
				const scanParameters = {
					employee: item,
					callbackError: () => {
						if( mounted.current ) {
							setScanInMutating(false);
						}
					},
					callbackStart: setScanInMutating.bind( this, true ),
					callbackSuccess: () => {
						if( mounted.current ) {
							setScanInMutating(false);
							fetchEmployees();
						}
					}
				};

				if ( _.get( item, 'is_in_office', constantsActive.NO ) === constantsActive.YES ) {
					scanInButton = (
						<Tag
							key={ HelperString.hash( `meeting-room-details-tag-scanout`, item ) }
							appearance={ tagAppearances.ORANGE }
							style={ styles.tag }
							onPress={ scanOut.bind( this, scanParameters ) }>

							{ i18n.t( 'basic.scan_out' ) }
						</Tag>
					);
				} else {
					scanInButton = (
						<Tag
							key={ HelperString.hash( `meeting-room-details-tag-scanin`, item ) }
							appearance={ tagAppearances.ORANGE }
							style={ styles.tag }
							onPress={ scanIn.bind( this, scanParameters ) }>

							{ i18n.t( 'basic.scan_in' ) }
						</Tag>
					);
				}
			} else {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-spinner`, item ) }
						appearance={tagAppearances.ORANGE}
						style={ styles.tag }>

						<Spinner style={ styles.spinnerWidth10Height10 } />
					</Tag>
				);
			}
		} else {
			if( _.get( item, 'phonenumber', null ) !== null ) {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-phonenumber`, item ) }
						appearance={ tagAppearances.GREY }
						style={ styles.tag }
						onPress={ callPhone.bind( this, { employee: item, which: constantsPhonenumbers.PHONENUMBER } )}>

						{ _.get( item, 'phonenumber', null ) }
					</Tag>
				);
			}

			if( _.get( item, 'landline_phonenumber', null ) !== null ) {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-landline`, item ) }
						appearance={ tagAppearances.GREY }
						style={ styles.tag }
						onPress={ callPhone.bind( this, { employee: item, which: constantsPhonenumbers.LANDLINE_PHONENUMBER } )}>

						{ _.get( item, 'landline_phonenumber', null ) }
					</Tag>
				);
			}
		}

		return	(
			<ListItem
				key={ HelperString.hash( `meeting-room-details-list-item`, item ) }>

				<EmployeeCard
					mode={ EmployeeCardModes.listItem }
					showHeart={ false }
					navigation={ navigation }
					employee={ item }>

					<View style={ styles.tagRow }>
						{ hints }
						{ scanInButton }
					</View>
				</EmployeeCard>
			</ListItem>
		)
	};

	let content = <LoadingComponent />
	if( !visibleEmployeesLoading || _.size( visibleEmployees ) > 0 ) {
		content = (
			<ThemedList
				data={ Object.values( visibleEmployees ) }
				dividerEnabled={ true }
				renderItem={ renderItem.bind( this ) }
			/>
		)
	}

	if( error ) {
		content = (
			<View>

				<Text>{ i18n.t('management:generic.load_error') }</Text>
				<Button onPress={ fetchEmployees }>

					{ i18n.t('management:generic.retry') }
				</Button>
			</View>
		);
	}

	return (
		<ThemedModal
			visible={ true }
			setVisible={ onClose }
			title={ title }
			subtitle={ subtitle }>

			{ content }
		</ThemedModal>
	)
}

export const styles = StyleSheet.create({
	spinnerWidth10Height10: {
		width: 10,
		height: 10,
	},
	tag: {
		marginRight: 5,
		flexDirection: 'column',
		alignItems: 'flex-start',
		textAlign: 'center'
	},
	tagRow: {
		marginTop: 5,
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
});

export default ManagementDashboardModalMeetingRoomDetails;
